import { useMemo, useState } from 'react';
import { PersonButton } from '@/features/people/PersonDrawer';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { CheckCheck, CircleCheck, ClipboardCheck, Megaphone, Search, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ackPageHref, filterAcks, isAckPending, type AckBox, type AckDueFilter, type AckListItem } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { useWorkspaceId } from '@/lib/hooks';
import { Button, Card, EmptyState, FilterBar, Input, PageHeader, Select, Spinner, Tabs, ask } from '@/components/ui';
import { ackListKey } from './Acks';
import { RelativeTime } from '@/components/time/RelativeTime';
import { useMobileLayout } from '@/mobile/layout';
import { useModuleOn } from '@/lib/modules';

/** t612u: Policies items addressed to me that I still have to acknowledge or read (same cache as the Policies area). */
export function usePolicyAcks() {
  const ws = useWorkspaceId();
  const on = useModuleOn('policies');
  const q = useQuery({ queryKey: ['policies', ws ?? '', 'mine'], queryFn: () => api.policies.mine(ws!), enabled: Boolean(ws) && on, refetchInterval: 120_000 });
  return useMemo(() => (on ? (q.data ?? []).filter(p => p.state === 'to_ack' || p.state === 'to_read') : []), [on, q.data]);
}

/** Older knowledge base policy articles still waiting for my acknowledgement (#24a). */
function useKbPolicyAcks() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['knowledge', ws, 'pending-policies'], queryFn: () => api.kb.pendingPolicies(ws!), enabled: Boolean(ws), refetchInterval: 120_000 }).data ?? [];
}

/** Policies, older knowledge base policies and announcements waiting for me (#22, #24, t612u), as plain rows for the page and Home. */
export function useContentAckRows(): { key: string; to: string; title: string; sub: string; action: string; kind: 'policy' | 'announcement' }[] {
  const ws = useWorkspaceId();
  const policies = usePolicyAcks();
  const kb = useKbPolicyAcks();
  const ann = useQuery({ queryKey: ['knowledge', ws, 'ann-acks'], queryFn: () => api.announcements.list(ws!, { view: 'feed', limit: 100 }), enabled: Boolean(ws) });
  return [
    ...policies.map(p => ({ key: `p-${p.id}`, to: `/policies/${p.id}`, title: p.title, sub: `Policy, version ${p.version} · ${p.type_name}`, kind: 'policy' as const,
      action: p.state === 'to_ack' ? 'Read and acknowledge' : 'Read' })),
    ...kb.map(p => ({ key: `k-${p.id}`, to: `/kb/${p.id}`, title: p.title, sub: `Policy, version ${p.published_version}${p.collection_name ? ` · ${p.collection_name}` : ''}`, kind: 'policy' as const,
      action: 'Read and acknowledge' })),
    ...(ann.data ?? []).filter(a => a.ack_required).map(a => ({ key: `a-${a.id}`, to: `/announcements/${a.id}`, title: a.title, kind: 'announcement' as const, action: 'Read and acknowledge',
      sub: `Announcement from ${a.author_name}${a.ack_due_at ? ` · due ${DateTime.fromISO(a.ack_due_at).setZone('Europe/London').toFormat('d LLL, HH:mm')}` : ''}` })),
  ];
}

/** Pending policy and announcement acknowledgements (#22, #24, t612u), for the sidebar badge and the page. */
export function useContentAckCount() {
  const ws = useWorkspaceId();
  const policies = usePolicyAcks();
  const kb = useKbPolicyAcks();
  const home = useQuery({ queryKey: ['knowledge', ws, 'ann-home'], queryFn: () => api.announcements.home(ws!), enabled: Boolean(ws), refetchInterval: 120_000 });
  return policies.length + kb.length + (home.data?.ack_pending ?? 0);
}

export function useAckList(box: AckBox) {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ackListKey(ws ?? '', box), queryFn: () => api.acks(ws!, box), enabled: Boolean(ws), refetchInterval: 60_000 });
}

const dueText = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone('Europe/London').toFormat('ccc d LLL, HH:mm');

function Row({ i, requested }: { i: AckListItem; requested?: boolean }) {
  const done = !requested && !isAckPending(i);
  const overdue = !done && i.due_at && Date.parse(i.due_at) < Date.now();
  // t239u: on the phone a row opens the acknowledgement's status page.
  const phone = useMobileLayout();
  return (
    <Link to={phone ? ackPageHref(i.message_id) : i.link} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60">
      {requested ? <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent-fg" /> : done ? <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <ShieldAlert className={clsx('mt-0.5 h-4 w-4 shrink-0', overdue ? 'text-rose-600' : 'text-orange-700')} />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">{i.preview}</div>
        <div className="text-xs text-slate-500"><PersonButton userId={i.author_id} name={i.author_name} className="rounded hover:underline">{i.author_name}</PersonButton> in {i.conversation_name}{i.parent_id ? ' (thread)' : ''} · <RelativeTime iso={i.posted_at} /></div>
      </div>
      {done && <span className="shrink-0 text-xs text-slate-500" data-testid="ack-done">{i.status === 'changed' ? 'Acknowledged an earlier version' : 'Acknowledged'}{i.acknowledged_at && <> <RelativeTime iso={i.acknowledged_at} /></>}</span>}
      {!requested && !done && i.read === false && <span className="shrink-0 text-[10px] font-medium text-slate-600 dark:text-slate-300">Unread</span>}
      {requested && i.counts && <span className="shrink-0 text-xs font-medium tabular-nums">{i.counts.acknowledged}/{i.counts.total}</span>}
      {i.due_at && !done && <span className={clsx('shrink-0 text-xs', overdue ? 'font-semibold text-rose-600' : 'text-slate-500')}>{overdue ? 'Overdue · ' : 'Due '}{dueText(i.due_at)}</span>}
    </Link>
  );
}

/** Home widget: outstanding acknowledgements. Hidden when there are none. */
export function AckWidget() {
  const q = useAckList('pending');
  const items = q.data?.items ?? [];
  // t612u: policies and announcements waiting for me come first, as the widget's description says.
  const content = useContentAckRows();
  const n = items.length + content.length;
  if (!n) return null;
  const shown = content.slice(0, 5);
  return (
    <Card title={<span className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-orange-700" />Needs my acknowledgement ({n})</span>}
      actions={<Link to="/acknowledgements" className="text-xs font-medium link" aria-label={`View ${n} ${n === 1 ? 'acknowledgement' : 'acknowledgements'} waiting for you`} data-testid="home-stat" data-stat="acks-pending">View all {n}</Link>}>
      <div className="-mx-4 -my-4 divide-y divide-slate-100 dark:divide-slate-800">
        {shown.map(r => <Link key={r.key} to={r.to} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60" data-testid="ack-content-row">
          {r.kind === 'policy' ? <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" /> : <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />}
          <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{r.title}</span><span className="block truncate text-xs text-slate-500">{r.sub}</span></span></Link>)}
        {items.slice(0, Math.max(0, 5 - shown.length)).map(i => <Row key={i.message_id} i={i} />)}
      </div>
    </Card>
  );
}

type Kind = 'all' | 'messages' | 'content';

export default function AcksPage() {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const toast = useUi(s => s.toast);
  const [tab, setTab] = useState<'mine' | 'requested'>('mine');
  const [due, setDue] = useState<AckDueFilter>('all');
  const [kind, setKind] = useState<Kind>('all');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const mine = useAckList('mine');
  const pendingCount = (mine.data?.items ?? []).filter(isAckPending).length;
  const requested = useAckList('requested');
  const content = useContentAckCount();
  const q = tab === 'mine' ? mine : requested;
  const shown = useMemo(() => {
    const t = text.trim().toLowerCase();
    return filterAcks(q.data?.items ?? [], { due }).filter(i => !t || `${i.preview} ${i.author_name} ${i.conversation_name}`.toLowerCase().includes(t));
  }, [q.data, due, text]);
  const readable = tab === 'mine' ? shown.filter(i => isAckPending(i) && i.read) : [];
  const todo = tab === 'mine' ? shown.filter(isAckPending) : shown;
  const done = tab === 'mine' ? shown.filter(i => !isAckPending(i)) : [];
  const active = (due !== 'all' ? 1 : 0) + (kind !== 'all' ? 1 : 0) + (text.trim() ? 1 : 0);

  const ackAllRead = async () => {
    if (!readable.length || !await ask({ title: `Acknowledge ${readable.length} message${readable.length > 1 ? 's' : ''} you've read?`, confirmLabel: 'Acknowledge', danger: false,
      body: 'Only messages you have already scrolled past in their conversation are included. Unread ones and thread replies stay here until you open them.' })) return;
    setBusy(true);
    try {
      const r = await api.acknowledgeMany(ws!, readable.map(i => i.message_id));
      toast(`${r.acknowledged.length} acknowledged${r.skipped.length ? ` · ${r.skipped.length} skipped (open them to acknowledge)` : ''}`, 'success');
      await qc.invalidateQueries({ queryKey: ['acks', ws] });
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader icon={<ClipboardCheck className="h-5 w-5" />} title="My acknowledgements"
        actions={tab === 'mine' && pendingCount > 0
          ? <Button variant="secondary" onClick={() => void ackAllRead()} loading={busy} disabled={!readable.length} data-testid="ack-all-read"><CheckCheck className="h-4 w-4" />Acknowledge all read{readable.length ? ` (${readable.length})` : ''}</Button>
          : undefined} />
      <Tabs className="px-3 sm:px-6" value={tab} onChange={setTab} tabs={[{ id: 'mine', label: 'Asked of me', count: pendingCount + content }, { id: 'requested', label: 'Requested by me' }]} />
      <div className="scroll-thin flex-1 space-y-6 overflow-y-auto p-6">
        <FilterBar active={active} onClear={() => { setDue('all'); setKind('all'); setText(''); }}>
          <label className="relative block w-64 max-w-full">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" aria-hidden />
            <Input className="pl-8" placeholder="Search messages, people, chats" aria-label="Search acknowledgements" value={text} onChange={e => setText(e.target.value)} />
          </label>
          <Select aria-label="Due" className="w-48" value={due} onChange={e => setDue(e.target.value as AckDueFilter)} data-testid="ack-due">
            <option value="all">Any due date</option><option value="overdue">Overdue</option><option value="due_soon">Due within 24 hours</option><option value="no_due">No due date</option>
          </Select>
          {tab === 'mine' && <Select aria-label="Type" className="w-52" value={kind} onChange={e => setKind(e.target.value as Kind)} data-testid="ack-kind">
            <option value="all">Messages, policies and posts</option><option value="messages">Chat messages</option><option value="content">Policies and announcements</option>
          </Select>}
        </FilterBar>
        {tab === 'mine' && kind !== 'messages' && due === 'all' && !text.trim() && <ContentAcks />}
        {(tab === 'requested' || kind !== 'content') && <Card>
          {q.isLoading ? <Spinner /> : !q.data?.items.length
            ? <EmptyState title={tab === 'mine' ? 'Nothing to acknowledge' : "You haven't asked anyone to acknowledge a message"}>{tab === 'mine' ? "You're up to date." : 'Use “Require acknowledgement” on a message, or the shield button when posting.'}</EmptyState>
            : !shown.length ? <EmptyState title="Nothing matches these filters" />
            : <div className="-mx-4 -my-4 divide-y divide-slate-100 dark:divide-slate-800" data-testid="ack-list">
                {todo.map(i => <Row key={i.message_id} i={i} requested={tab === 'requested'} />)}
                {done.length > 0 && <div className="bg-slate-50 px-4 py-1.5 text-xs font-medium text-slate-500 dark:bg-slate-800/50" data-testid="ack-done-heading">Acknowledged ({done.length})</div>}
                {done.map(i => <Row key={i.message_id} i={i} />)}
              </div>}
        </Card>}
      </div>
    </div>
  );
}

/** Policies (#24a, t612u) and announcements (#22) waiting for my acknowledgement. */
function ContentAcks() {
  const rows = useContentAckRows();
  if (!rows.length) return null;
  return (
    <Card title="Policies and announcements">
      <div className="-mx-4 -my-4 divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map(r => <Link key={r.key} to={r.to} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid="ack-content-row">
          {r.kind === 'policy' ? <ShieldCheck className="h-5 w-5 text-slate-500" /> : <Megaphone className="h-5 w-5 text-slate-500" />}
          <span className="flex-1"><span className="font-medium">{r.title}</span><span className="block text-xs text-slate-500">{r.sub}</span></span><span className="text-sm link">{r.action}</span></Link>)}
      </div>
    </Card>
  );
}
